/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { Copy, Maximize2, Minimize2, FileText, Check } from 'lucide-react';
import { cn } from 'src/lib/utils';

const EDITOR_CLS = [
  'w-full px-3.5 py-3.5 border border-border rounded-b-lg bg-card',
  'text-[13.5px] text-foreground-strong leading-[1.75] outline-none focus:border-primary font-[inherit]',
  '[&_h1]:text-[20px] [&_h1]:font-bold [&_h1]:tracking-tight [&_h1]:mt-5 [&_h1]:mb-2',
  '[&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-1.5',
  '[&_h3]:text-[14px] [&_h3]:font-medium [&_h3]:mt-4 [&_h3]:mb-1',
  '[&_p]:my-1.5',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-1 [&_ul]:mb-3',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mt-1 [&_ol]:mb-3',
  '[&_li]:mb-0.5 [&_strong]:font-bold [&_em]:italic',
].join(' ');

const applyToEditor = (ref, content) => {
  if (!ref.current) return;
  if (content && /<[a-z][\s\S]*>/i.test(content)) {
    ref.current.innerHTML = content;
  } else {
    ref.current.innerText = content || '';
  }
};

const Toolbar = ({ editorRef, onUpdate, onExpand, roundedTop = true }) => {
  const [active, setActive] = useState({});

  // Reflect the current selection's formatting on the toolbar
  const refresh = () => {
    const el = editorRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode || !el.contains(sel.anchorNode)) return;
    let block = '';
    try {
      block = (document.queryCommandValue('formatBlock') || '').toLowerCase();
    } catch (e) {
      block = '';
    }
    setActive({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      ul: document.queryCommandState('insertUnorderedList'),
      ol: document.queryCommandState('insertOrderedList'),
      block,
    });
  };

  useEffect(() => {
    document.addEventListener('selectionchange', refresh);
    return () => document.removeEventListener('selectionchange', refresh);
  }, []);

  const exec = (cmd) => {
    editorRef.current?.focus();
    // A heading block and a list are mutually exclusive — toggling a list
    // on while the caret is inside a heading makes execCommand wrap the
    // WHOLE resulting <ul>/<ol> inside the <h1>/<h2>/<h3> instead of
    // converting just that line (confirmed live:
    // "<h2><ul><li>text</li></ul></h2>"), which htmlToMarkdown can't make
    // sense of and silently drops the list entirely on save — it saved as
    // "## text" with no list marker at all. Clearing the heading first
    // keeps the two formats from ever nesting like that.
    if (
      (cmd === 'insertUnorderedList' || cmd === 'insertOrderedList') &&
      ['h1', 'h2', 'h3'].includes(
        (document.queryCommandValue('formatBlock') || '').toLowerCase()
      )
    ) {
      document.execCommand('formatBlock', false, '<div>');
    }
    document.execCommand(cmd, false, null);
    onUpdate(editorRef.current?.innerHTML || '');
    refresh();
  };
  const block = (tag) => {
    editorRef.current?.focus();
    // Same mutual exclusion the other direction — applying a heading while
    // the caret is inside a list item wraps the entire <ul>/<ol> inside the
    // heading tag instead of converting just that <li>, which is equally
    // unrecoverable on save. Exit the list first so the heading lands on a
    // plain block instead.
    if (document.queryCommandState('insertUnorderedList')) {
      document.execCommand('insertUnorderedList', false, null);
    } else if (document.queryCommandState('insertOrderedList')) {
      document.execCommand('insertOrderedList', false, null);
    }
    // formatBlock needs the bracketed form (<h1>) to work across browsers —
    // Firefox and some engines silently ignore a bare "h1". Toggle back to a
    // plain block when the caret is already in that heading level.
    const current = (
      document.queryCommandValue('formatBlock') || ''
    ).toLowerCase();
    document.execCommand(
      'formatBlock',
      false,
      current === tag ? '<div>' : `<${tag}>`
    );
    onUpdate(editorRef.current?.innerHTML || '');
    refresh();
  };
  const btn = (handler, children, isActive) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        handler();
      }}
      className={`px-2 py-1 rounded text-[12px] transition-all ${
        isActive
          ? 'bg-brand-bg-subtle text-primary font-semibold'
          : 'text-muted-foreground hover:bg-border/60 hover:text-foreground-strong'
      }`}
    >
      {children}
    </button>
  );
  const sep = <div className="w-px bg-border mx-1.5 my-1" />;
  return (
    <div
      className={cn(
        'flex gap-px p-1.5 border border-b-0 bg-muted border-border',
        roundedTop && 'rounded-t-lg'
      )}
    >
      {btn(() => exec('bold'), <b>B</b>, active.bold)}
      {btn(() => exec('italic'), <i>I</i>, active.italic)}
      {btn(
        () => exec('underline'),
        <span className="underline">U</span>,
        active.underline
      )}
      {sep}
      {btn(
        () => block('h1'),
        <span className="font-semibold">H1</span>,
        active.block === 'h1'
      )}
      {btn(
        () => block('h2'),
        <span className="font-semibold">H2</span>,
        active.block === 'h2'
      )}
      {btn(
        () => block('h3'),
        <span className="font-semibold">H3</span>,
        active.block === 'h3'
      )}
      {sep}
      {btn(() => exec('insertUnorderedList'), '• List', active.ul)}
      {btn(() => exec('insertOrderedList'), '1. List', active.ol)}
      {onExpand && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onExpand();
          }}
          aria-label="Expand"
          title="Expand"
          className="ml-auto flex items-center justify-center p-1 rounded text-muted-foreground hover:bg-border/60 hover:text-foreground-strong transition-all"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

// `variant="sticky"` pins the same editor to the viewport via `position:
// fixed` instead of flowing in-page — e.g. InterviewBrief.jsx's "Notes"
// panel. A thin label header (`title`, plus the Expand button moved up off
// the toolbar) sits above the toolbar and doubles as the drag handle (see
// `stickyHeader` below), so dragging no longer shares a hit target with the
// format buttons and still works even while `children` (a loading/error
// state) replaces the toolbar+editable area beneath it. A small grip in the
// bottom-right corner (see `resizeHandle`) lets the note be freely resized
// — independent of, not a replacement for, the header's Expand button,
// which still jumps to the dedicated full-screen modal. The caller owns
// open/close (`open`, driven by its own header toggle elsewhere) and the
// footer's buttons/content (`footer`).
const MarkdownEditor = ({
  value = '',
  onChange,
  minHeight = 160,
  maxHeight,
  title = '',
  className = '',
  variant = 'inline',
  open = true,
  footer = null,
  children,
}) => {
  const isSticky = variant === 'sticky';
  const editorRef = useRef(null);
  const fullEditorRef = useRef(null);
  const lastSynced = useRef('');
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  // Sticky-only — whether the editable area itself currently has focus.
  // Starts false: the toolbar stays tucked away under the header until the
  // note is actually clicked into, not the instant it opens.
  const [isActive, setIsActive] = useState(false);

  // Sticky-only drag position — see `stickyHeader` below for the actual
  // drag handle. Plain component state, not persisted anywhere, same as a
  // real sticky note has no memory of where it was last stuck.
  const [pos, setPos] = useState(() => ({
    top: 84,
    left: Math.max(24, window.innerWidth - 400),
  }));
  const dragState = useRef(null);
  const handleDragStart = (e) => {
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origTop: pos.top,
      origLeft: pos.left,
    };
    const handleMove = (ev) => {
      if (!dragState.current) return;
      const { startX, startY, origTop, origLeft } = dragState.current;
      setPos({
        left: Math.min(
          Math.max(origLeft + (ev.clientX - startX), 8),
          window.innerWidth - 60
        ),
        top: Math.min(
          Math.max(origTop + (ev.clientY - startY), 8),
          window.innerHeight - 60
        ),
      });
    };
    const handleUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  // Sticky-only free resize, dragged from either bottom corner grip (see
  // `resizeHandle`/`resizeHandleLeft` below). `height: null` means "not
  // resized yet" — the editable area keeps using the caller's own
  // minHeight/maxHeight (with the usual overflow scroll past maxHeight)
  // until the user actually drags a grip once, at which point an explicit
  // height takes over and the area always scrolls internally past it, same
  // as a real floating window's resize handle.
  const [size, setSize] = useState({ width: 380, height: null });
  const resizeState = useRef(null);
  // Bottom-right: top-left corner (`pos`) stays fixed, width/height just
  // grow or shrink from there.
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height ?? editorRef.current?.offsetHeight ?? minHeight,
    };
    const handleMove = (ev) => {
      if (!resizeState.current) return;
      const { startX, startY, startWidth, startHeight } = resizeState.current;
      setSize({
        width: Math.max(
          280,
          Math.min(
            startWidth + (ev.clientX - startX),
            window.innerWidth - pos.left - 16
          )
        ),
        height: Math.max(
          120,
          Math.min(
            startHeight + (ev.clientY - startY),
            window.innerHeight - pos.top - 16
          )
        ),
      });
    };
    const handleUp = () => {
      resizeState.current = null;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };
  // Bottom-left: the mirror image — the RIGHT edge (startLeft + startWidth)
  // is what stays fixed here instead, so both `size.width` and `pos.left`
  // move together as the left edge is dragged; deriving the new left from
  // the already-clamped width (rather than clamping each independently)
  // keeps the right edge from drifting even once width hits its min/max.
  const handleResizeStartLeft = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height ?? editorRef.current?.offsetHeight ?? minHeight,
      startLeft: pos.left,
    };
    const handleMove = (ev) => {
      if (!resizeState.current) return;
      const { startX, startY, startWidth, startHeight, startLeft } =
        resizeState.current;
      const rightEdge = startLeft + startWidth;
      const newWidth = Math.max(
        280,
        Math.min(startWidth - (ev.clientX - startX), rightEdge - 8)
      );
      setSize({
        width: newWidth,
        height: Math.max(
          120,
          Math.min(
            startHeight + (ev.clientY - startY),
            window.innerHeight - pos.top - 16
          )
        ),
      });
      setPos((prev) => ({ ...prev, left: rightEdge - newWidth }));
    };
    const handleUp = () => {
      resizeState.current = null;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  // Sync external value changes into the DOM without clobbering user keystrokes
  useEffect(() => {
    if (value !== lastSynced.current) {
      applyToEditor(editorRef, value);
      lastSynced.current = value;
    }
  }, [value]);

  // Sync the right editor when the modal opens or closes
  useEffect(() => {
    if (expanded) {
      applyToEditor(fullEditorRef, value);
    } else {
      applyToEditor(editorRef, value);
      lastSynced.current = value;
    }
    // value intentionally excluded — effect only runs on expanded toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // Escape closes modal
  useEffect(() => {
    if (!expanded) return;
    const handler = (e) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [expanded]);

  const handleChange = (ref) => {
    const html = ref.current?.innerHTML || '';
    lastSynced.current = html;
    onChange?.(html);
  };

  // Without this, pressing Enter at the end of a heading can leave the new
  // line formatted as that same heading (contentEditable's native Enter
  // behavior here is inconsistent across browsers, and this component never
  // sets a default paragraph separator) — every subsequent line you type
  // keeps inheriting the heading's block tag until you notice and manually
  // click the heading button again to turn it off. Worse, text styled
  // Bold while still inside that heading doesn't actually turn bold: the
  // heading is already visually bold via CSS, so the browser's own
  // "is this already bold?" check short-circuits and the Bold command
  // silently no-ops instead of wrapping anything. Every other rich text
  // editor scopes a heading to the line you typed it on, so after the
  // browser inserts its new line, force it back to plain if it's still a
  // heading — deferred a tick so it runs after that insertion lands.
  const resetHeadingOnEnter = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    let block = '';
    try {
      block = (document.queryCommandValue('formatBlock') || '').toLowerCase();
    } catch {
      block = '';
    }
    if (block !== 'h1' && block !== 'h2' && block !== 'h3') return;
    setTimeout(() => {
      let current = '';
      try {
        current = (
          document.queryCommandValue('formatBlock') || ''
        ).toLowerCase();
      } catch {
        current = '';
      }
      if (current === 'h1' || current === 'h2' || current === 'h3') {
        document.execCommand('formatBlock', false, '<div>');
      }
    }, 0);
  };

  const handleCopy = () => {
    const el = document.createElement('div');
    el.innerHTML = value;
    navigator.clipboard?.writeText(el.innerText || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Once the grip has set an explicit height, that always wins over the
  // caller's minHeight/maxHeight and always scrolls internally — same as
  // any real window, resizing never shrinks past what the user dragged.
  const resizedHeight = isSticky ? size.height : null;

  const toolbarEl = (
    <Toolbar
      editorRef={editorRef}
      onUpdate={() => handleChange(editorRef)}
      onExpand={isSticky ? undefined : () => setExpanded(true)}
      roundedTop={!isSticky}
    />
  );

  const editorBody = (
    <>
      {isSticky ? (
        // Tucked away (grid-template-rows 0fr) until the editable area
        // below gets focus, then grows to its natural height (1fr) — the
        // same "animate toward an unmeasured height" trick
        // AutoCollapseButton.jsx/InterviewBrief.jsx's QuestionCard already
        // use elsewhere in this app, so it reads as the toolbar dropping
        // down out of the header rather than a generic height snap.
        // `overflow-hidden` on the inner wrapper (not the outer grid
        // itself) is what lets it actually shrink past its own content's
        // height instead of stopping at the toolbar's intrinsic size.
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-out"
          style={{ gridTemplateRows: isActive ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">{toolbarEl}</div>
        </div>
      ) : (
        toolbarEl
      )}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => handleChange(editorRef)}
        onKeyDown={resetHeadingOnEnter}
        onFocus={() => isSticky && setIsActive(true)}
        onBlur={() => isSticky && setIsActive(false)}
        className={cn(
          EDITOR_CLS,
          (maxHeight || resizedHeight) && 'overflow-y-auto',
          // Stays bordered-but-square on the bottom when a footer follows,
          // so the two read as one continuous card instead of two stacked
          // rounded boxes.
          isSticky && footer && 'rounded-b-none border-b-0',
          // Sticky stays fully neutral even while focused/active — no blue
          // anywhere on this widget, per explicit request — unlike the
          // inline variant (CreatePersona.jsx/OpportunityActions.jsx),
          // which keeps EDITOR_CLS's own focus:border-primary as the
          // normal "this field is focused" affordance every other input in
          // the app uses.
          isSticky && 'focus:border-border'
        )}
        style={
          resizedHeight ? { height: resizedHeight } : { minHeight, maxHeight }
        }
      />
    </>
  );

  // Sticky-only label bar + drag handle, sitting above the toolbar. Square
  // bottom corners (fuses with the toolbar's own now-square top) and
  // rounded top corners, so it reads as the top edge of one continuous
  // card rather than a second stacked box. The Expand button lives here
  // now instead of in the toolbar's own button row (see editorBody above),
  // with the same stopPropagation trick Toolbar's own copy used, so
  // clicking it doesn't also start a drag.
  const stickyHeader = isSticky && (
    <div
      onMouseDown={handleDragStart}
      className="flex cursor-grab select-none items-center justify-between gap-2 rounded-t-lg border border-b-0 border-border bg-muted px-3 py-2 text-[12px] font-semibold text-foreground-strong active:cursor-grabbing"
    >
      <span className="truncate">{title || 'Note'}</span>
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setExpanded(true);
        }}
        aria-label="Expand"
        title="Expand"
        className="flex shrink-0 items-center justify-center rounded p-0.5 text-muted-foreground hover:bg-border/60 hover:text-foreground-strong transition-all"
      >
        <Maximize2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  // Bottom corner grips for free resize (see `handleResizeStart`/
  // `handleResizeStartLeft` above) — a few diagonal lines, the same visual
  // shorthand most desktop windows use for "drag to resize", rather than an
  // icon that'd need its own explanation. The gradient angle mirrors across
  // the two (135deg/45deg) so each one's stripes visually point along its
  // own drag diagonal, matching its cursor (nwse/nesw).
  const resizeHandle = isSticky && (
    <div
      onMouseDown={handleResizeStart}
      role="presentation"
      aria-hidden="true"
      className="absolute bottom-0.5 right-0.5 h-3 w-3 cursor-nwse-resize"
      style={{
        backgroundImage:
          'repeating-linear-gradient(135deg, hsl(var(--border)) 0, hsl(var(--border)) 1px, transparent 1px, transparent 3px)',
      }}
    />
  );
  const resizeHandleLeft = isSticky && (
    <div
      onMouseDown={handleResizeStartLeft}
      role="presentation"
      aria-hidden="true"
      className="absolute bottom-0.5 left-0.5 h-3 w-3 cursor-nesw-resize"
      style={{
        backgroundImage:
          'repeating-linear-gradient(45deg, hsl(var(--border)) 0, hsl(var(--border)) 1px, transparent 1px, transparent 3px)',
      }}
    />
  );

  // Same full-screen modal for both variants — reusing it is what lets the
  // sticky variant offer a bigger editing view without any resize logic of
  // its own, via the toolbar's existing "Expand" button.
  const fullScreenModal = expanded && (
    <div
      className="fixed inset-0 z-[1000] bg-[rgba(10,30,50,.45)] backdrop-blur-[2px] flex items-center justify-center p-8"
      onClick={(e) => {
        if (e.target === e.currentTarget) setExpanded(false);
      }}
    >
      <div
        className="bg-card rounded-2xl w-full max-w-[1280px] flex flex-col overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,.32)]"
        style={{ height: 'min(90vh, 920px)' }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2.5 text-[15px] font-bold text-foreground-strong tracking-[-0.02em]">
            <FileText className="w-[18px] h-[18px] text-primary" />
            {title || 'Editor'}
          </div>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-brand-bg-subtle hover:text-primary hover:border-primary/20 transition-all"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 mt-4 flex-shrink-0">
          <Toolbar
            editorRef={fullEditorRef}
            onUpdate={() => handleChange(fullEditorRef)}
          />
        </div>
        <div
          ref={fullEditorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={() => handleChange(fullEditorRef)}
          onKeyDown={resetHeadingOnEnter}
          className={`flex-1 mx-5 mb-[18px] overflow-y-auto overflow-x-hidden min-h-0 min-w-0 break-words ${EDITOR_CLS} !w-auto border-t-0 rounded-t-none`}
        />
        <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-border flex-shrink-0">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-card text-[13px] font-medium text-muted-foreground hover:bg-muted transition-all"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-[#0D9668]" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="px-4 py-2 rounded-lg bg-primary text-white text-[13px] font-semibold hover:bg-[#1B5CA0] transition-all"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );

  if (isSticky) {
    return (
      <>
        <div
          className={cn(
            'fixed z-40 drop-shadow-lg',
            !open && 'hidden',
            className
          )}
          style={{ top: pos.top, left: pos.left, width: size.width }}
          role="dialog"
          aria-label={title || 'Note'}
        >
          {stickyHeader}
          {children || editorBody}
          {footer && (
            <div className="flex items-center justify-between gap-2 rounded-b-lg border border-t-0 border-border bg-muted px-3 py-2.5">
              {footer}
            </div>
          )}
          {resizeHandle}
          {resizeHandleLeft}
        </div>
        {fullScreenModal}
      </>
    );
  }

  return (
    <>
      <div className={className}>{editorBody}</div>
      {fullScreenModal}
    </>
  );
};

export default MarkdownEditor;
